"use client";

import { motion } from "framer-motion";
import { useEyeAnimation } from "@/lib/hooks/useEyeAnimation";
import { useState, useRef, useCallback } from "react";

const EYE_REST_X = 75;
const EYE_REST_Y = 75;
const EYE_RADIUS = 6.25;

interface PeekIconProps {
  className?: string;
  isLookingAtText?: boolean;
}

export function PeekIcon({ className = "", isLookingAtText = false }: PeekIconProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();

    // Calculate eye center in screen coordinates
    const eyeScreenX = rect.left + (EYE_REST_X / 100) * rect.width;
    const eyeScreenY = rect.top + (EYE_REST_Y / 100) * rect.height;

    // Normalize mouse offset from eye center, clamped to -1..1
    const relativeX = Math.max(-1, Math.min(1, (e.clientX - eyeScreenX) / (rect.width / 2)));
    const relativeY = Math.max(-1, Math.min(1, (e.clientY - eyeScreenY) / (rect.height / 2)));

    setMousePosition({ x: relativeX, y: relativeY });
  }, []);

  const { animation, scale } = useEyeAnimation(isLookingAtText, isHovered, mousePosition);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ shapeRendering: "geometricPrecision" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {/* Background rounded square */}
      <path
        d="M78 0H22C9.84974 0 0 9.84974 0 22V78C0 90.1503 9.84974 100 22 100H78C90.1503 100 100 90.1503 100 78V22C100 9.84974 90.1503 0 78 0Z"
        fill="#FF6B7B"
      />

      {/* White peek shape */}
      <path
        d="M100 43.75V100H43.75C43.75 100 43.75 68.75 62.5 56.25C75 46.875 100 43.75 100 43.75Z"
        fill="white"
      />

      {/* White triangle */}
      <path
        d="M56.25 81.25L43.75 100H68.75L56.25 81.25Z"
        fill="white"
      />

      {/* Animated eye circle - controlled by state machine */}
      <motion.circle
        cx={EYE_REST_X}
        cy={EYE_REST_Y}
        r={EYE_RADIUS}
        fill="#FF6B7B"
        animate={{
          cx: EYE_REST_X + animation.x,
          cy: EYE_REST_Y + animation.y,
          r: EYE_RADIUS * scale,
        }}
        transition={animation.transition}
      />
    </svg>
  );
}
